import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';

import { Form, Input, Switch } from '@arco-design/web-react';
import type { FormInstance } from '@arco-design/web-react/es/Form';
import Col from '@arco-design/web-react/es/Grid/col';
import Row from '@arco-design/web-react/es/Grid/row';

import { type ImageRegistryPayload, applyRegistryLeavesToForm } from '@/delivery-type-plugin/capabilities/image/dockerProbe';

import styles from './index.module.less';

const FormItem = Form.Item;

export const REGISTRY_USERNAME_FIELD = 'registry.username';
export const REGISTRY_PASSWORD_FIELD = 'registry.password';
export const REGISTRY_SERVER_FIELD = 'registry.server';
/** docker-probe registryAuth.insecureTLS：开启后填写账密 */
export const REGISTRY_INSECURE_TLS_FIELD = 'registry.insecureTLS';

export interface ImagePullAuthFieldsProps {
  form?: FormInstance;
  usernameField?: string;
  passwordField?: string;
  serverField?: string;
  insecureTlsField?: string;
  disabled?: boolean;
  /** 节点抽屉等场景：默认关，开启后才展示账密 */
  enableAuthSwitch?: boolean;
}

function ImagePullAuthLeafHydrate({ form, prefix }: { form: FormInstance; prefix: string }) {
  const nested = Form.useWatch(prefix, form) as ImageRegistryPayload | undefined;
  useEffect(() => {
    applyRegistryLeavesToForm(form, nested, prefix);
  }, [form, nested, prefix]);
  return null;
}

function resolvePrefix(serverField: string): string {
  return serverField.endsWith('.server') ? serverField.slice(0, -'.server'.length) : 'registry';
}

/** 镜像拉取认证：可选开关 + 用户名 / 密码同一行 */
export function ImagePullAuthFields({
  form,
  usernameField = REGISTRY_USERNAME_FIELD,
  passwordField = REGISTRY_PASSWORD_FIELD,
  serverField = REGISTRY_SERVER_FIELD,
  insecureTlsField = REGISTRY_INSECURE_TLS_FIELD,
  disabled,
  enableAuthSwitch = false,
}: ImagePullAuthFieldsProps) {
  const { t } = useTranslation();
  const prefix = resolvePrefix(serverField);
  const authEnabled = Form.useWatch(insecureTlsField, form) === true;

  useEffect(() => {
    if (!form || !enableAuthSwitch || authEnabled) return;
    form.setFieldValue(usernameField, undefined);
    form.setFieldValue(passwordField, undefined);
  }, [authEnabled, enableAuthSwitch, form, passwordField, usernameField]);

  const credentialFields = (
    <Row gutter={16}>
      <Col span={12}>
        <FormItem
          field={usernameField}
          style={{ marginBottom: 0 }}
          disabled={disabled}
          rules={enableAuthSwitch && authEnabled ? [{ required: true, message: t('common:please.enter.username') }] : undefined}
        >
          <Input placeholder={t('common:please.enter.username')} disabled={disabled} autoComplete="off" />
        </FormItem>
      </Col>
      <Col span={12}>
        <FormItem
          field={passwordField}
          style={{ marginBottom: 0 }}
          disabled={disabled}
          rules={enableAuthSwitch && authEnabled ? [{ required: true, message: t('common:please.enter.password') }] : undefined}
        >
          <Input.Password placeholder={t('common:please.enter.password')} disabled={disabled} autoComplete="new-password" />
        </FormItem>
      </Col>
    </Row>
  );

  return (
    <>
      {form ? <ImagePullAuthLeafHydrate form={form} prefix={prefix} /> : null}
      {enableAuthSwitch ? (
        <div className={styles.authSwitchBlock}>
          <FormItem
            label={t('data-center:image.pull.auth.private')}
            layout="horizontal"
            labelAlign="left"
            labelCol={{ flex: 'none' }}
            wrapperCol={{ flex: 'none' }}
            className={authEnabled ? styles.switchItem : styles.switchItemCompact}
          >
            <FormItem field={insecureTlsField} triggerPropName="checked" initialValue={false} noStyle>
              <Switch disabled={disabled} />
            </FormItem>
          </FormItem>
          {authEnabled ? <div className={styles.credentials}>{credentialFields}</div> : null}
        </div>
      ) : (
        <FormItem label={t('data-center:image.pull.auth.private')}>{credentialFields}</FormItem>
      )}
      <FormItem field={serverField} hidden>
        <Input />
      </FormItem>
    </>
  );
}
